import '@atlaskit/css-reset/bundle.css';

import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import './index.css';
import { HelmetProvider } from 'react-helmet-async';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { EXPOSURE_RELAY_PATH } from '@rovo-extension/feature-gates-adapter';
import { ErrorBoundary } from './components/ErrorBoundary';
import ExposureRelayRoot from './components/exposure-relay/ExposureRelayRoot';
import { initializeSentry } from './bootstrap/sentry';
import { preloadChatBundles } from './bootstrap/preloadChatBundles';
import RequireAuth from './components/RequireAuth';
import { persister, queryClient } from './ExtensionApp';
import { AppIntlProvider } from './i18n/AppIntlProvider';
import { initUFO, UFOSuspenseFallback } from './analytics/react-ufo';
import { ExtensionInitTimingUFO } from './analytics/react-ufo/ExtensionInitTimingUFO';
import { ThemeProvider } from './hooks/useThemeContext';
import { PerformanceSLO, PerformanceSLOKeys } from './observability';
import { setLocalDevSessionCookie } from './config';

if (process.env.IS_LOCAL_DEV_ATLASPACK_BUILD === 'true') {
	void setLocalDevSessionCookie();
	// Compiled stylesheet extraction is off in the dev server, which lets a lazily-loaded
	// chunk re-inject `display: none` over the top-nav actions. See the file for details.
	void import('./dev-topnav-css-order-fix.css');
}

const HomeLoginPageLazy = React.lazy(
	() => import(/* webpackChunkName: "home-login" */ './components/home/HomeLoginPage'),
);

initializeSentry();

const container = document.getElementById('root');
const root = createRoot(container!);

const LazyApp = React.lazy(() => import('./App'));

// Component to handle extension timing data within UFO context
const ExtensionTimingHandler = (): React.JSX.Element => {
	return <ExtensionInitTimingUFO />;
};

function renderExtensionApp(): void {
	// Initialize UFO as early as possible to capture more initialization time
	initUFO();

	// Start the chat chunk downloads here so they overlap the auth and Statsig wait below.
	preloadChatBundles();

	root.render(
		<PerformanceSLO ufo={PerformanceSLOKeys.APPLICATION_BOOTSTRAP}>
			<ExtensionTimingHandler />
			<HelmetProvider>
				<AppIntlProvider>
					<ErrorBoundary>
						<BrowserRouter>
							<FlagsProvider>
								<PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
									<ThemeProvider>
										<Suspense fallback={<UFOSuspenseFallback name="main-app-loading" />}>
											<RequireAuth
												Component={LazyApp}
												LoginComponent={HomeLoginPageLazy}
												publicRoutes={[
													'/extension/welcome',
													'/extension/log-out',
													'/extension/ntp-logout-redirect',
												]}
											/>
										</Suspense>
									</ThemeProvider>
								</PersistQueryClientProvider>
							</FlagsProvider>
						</BrowserRouter>
					</ErrorBoundary>
				</AppIntlProvider>
			</HelmetProvider>
		</PerformanceSLO>,
	);
}

// The extension's hidden relay page loads this bundle too, but needs only Statsig: no UFO, chat
// preload or app shell.
if (window.location.pathname === EXPOSURE_RELAY_PATH) {
	root.render(<ExposureRelayRoot />);
} else {
	renderExtensionApp();
}
